import { useState, FC, ReactNode, ChangeEvent, useCallback } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { FieldProps } from 'formik';
import styled from 'styled-components';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { useAppSelector } from '../../lib';
import { configSelectors } from '../../store';
import { api } from '../../api';
import { ApiError } from '../errors';
import { t } from 'i18next';

export const WIX_MEDIA_DIALOG_OPEN_DELAY = 5000;

type ImagePickerFieldProps = FieldProps & {
  label?: ReactNode;
  imageVariants?: string[];
  row?: boolean;
  uploadUrl?: string;
  handleWixModalOpened?: () => void;
  renderPreview?: (imageUrl: string) => ReactNode;
};

const acceptedFiles = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
const maxFileSize = 5000000;

export const ImagePickerField: FC<ImagePickerFieldProps> = (props) => {
  const isWix = useAppSelector(configSelectors.isWix);

  if (isWix) {
    return <WixImagePicker {...props} />;
  }
  
  return <ImagePicker {...props} />;
};

const ImagePicker: FC<ImagePickerFieldProps> = ({
  imageVariants,
  renderPreview,
  label,
  field,
  form,
  row = false,
  uploadUrl = '',
}) => {
  const [uploadFile, uploadFileResult] = api.useUploadFileMutation();

  const updateImage = useCallback(
    (imageUrl: string) => {
      form.setFieldValue(field.name, imageUrl);
      form.submitForm();
    },
    [field.name, form]
  );

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] as Blob;

    form.setFieldError(field.name, '');

    if (!file) {
      return;
    }

    if (!acceptedFiles.includes(file.type)) {
      form.setFieldError(field.name, 'The file format is not supported. Use jpeg, png or gif');
      return;
    }

    if (file.size > maxFileSize) {
      form.setFieldError(field.name, 'File size must be less than 5MB');
      return;
    }

    const formData = new FormData();

    formData.append('file', file);
    formData.append('uploadUrl', uploadUrl);

    const result = await uploadFile(formData).unwrap();

    if (result.url) {
      updateImage(result.url);
    }
  };

  const error = form.getFieldMeta(field.name).error;

  return (
    <>
      {label && (
        <Label color="GrayText" row={row}>
          {label}
        </Label>
      )}

      {imageVariants && imageVariants.length > 0 && (
        <ImageVariantsWrapper>
          {imageVariants.map((imageUrl, index) => (
            <ImageVariantContainer
              imageUrl={imageUrl}
              key={index}
              width={60}
              height={60}
              onClick={() => updateImage(imageUrl)}
              selected={imageUrl === field.value}
            />
          ))}
        </ImageVariantsWrapper>
      )}

      {renderPreview && field.value ? (
        <PreivewWrapper>
          {renderPreview(field.value)}

          <DeletePreviewButton
            onClick={() => {
              updateImage('');
            }}
            variant="text"
            size="small"
          >
            Delete
          </DeletePreviewButton>
        </PreivewWrapper>
      ) : null}

      <Button
        component="label"
        variant="contained"
        startIcon={uploadFileResult.isLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
      >
        {t('pages.campaign.rouletteSettings.texts.button')}
        <VisuallyHiddenInput
          type="file"
          accept="image/*"
          onChange={handleChange}
          disabled={uploadFileResult.isLoading}
        />
      </Button>

      {uploadFileResult.isError && <ApiError error={uploadFileResult.error} />}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </>
  );
};

const WixImagePicker: FC<ImagePickerFieldProps> = ({
  imageVariants,
  handleWixModalOpened,
  renderPreview,
  label,
  field,
  form,
  row = false,
}) => {
  const [wixModalLoading, setWixModalLoading] = useState(false);

  const updateImage = (imageUrl: string) => {
    form.setFieldValue(field.name, imageUrl);
    form.submitForm();
  };

  const openWixModal = () => {
    setWixModalLoading(true);

    /**
     * После нажатия Cancel в WixMediaDialog не вызывается onCancel
     */
    setTimeout(() => {
      setWixModalLoading(false);
      handleWixModalOpened?.();
    }, WIX_MEDIA_DIALOG_OPEN_DELAY);

    Wix.Dashboard.openMediaDialog(Wix.Settings.MediaType.IMAGE, false, (data) => {
      updateImage(data.relativeUri);
    });
  };

  return (
    <>
      {label && (
        <Label color="GrayText" row={row}>
          {label}
        </Label>
      )}

      {imageVariants && imageVariants.length > 0 && (
        <ImageVariantsWrapper>
          {imageVariants.map((imageUrl, index) => (
            <ImageVariantContainer
              imageUrl={imageUrl}
              key={index}
              width={60}
              height={60}
              onClick={() => updateImage(imageUrl)}
              selected={imageUrl === field.value}
            />
          ))}
        </ImageVariantsWrapper>
      )}

      {renderPreview && field.value ? (
        <PreivewWrapper>
          {renderPreview(field.value)}

          <DeletePreviewButton
            onClick={() => {
              updateImage('');
            }}
            variant="text"
            size="small"
          >
            Delete
          </DeletePreviewButton>
        </PreivewWrapper>
      ) : null}

      <Button
        onClick={openWixModal}
        fullWidth={!row}
        disabled={wixModalLoading}
        variant="contained"
        sx={{ minWidth: 115 }}
      >
        {wixModalLoading ? <CircularProgress size={25} /> : <>Pick image</>}
      </Button>
    </>
  );
};

const ImageVariantsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
`;

const Label = styled(Typography)<{ row: boolean }>`
  ${(p) => (p.row ? 'margin-right: 8px' : 'margin-bottom: 8px')};
`;

const PreivewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const DeletePreviewButton = styled(Button)`
  margin-bottom: 8px;
`;

const VisuallyHiddenInput = styled.input`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1;
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  color: red;
  margin-top: 8px;
`;

export const ImageVariantContainer = styled.div<{
  imageUrl: string;
  width: number;
  height: number;
  selected?: boolean;
}>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(p) => `${p.width}px`};
  height: ${(p) => `${p.height}px`};
  background-image: url(${(p) => p.imageUrl});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;

  :hover {
    box-shadow: 0px 0px 0 1px rgba(255, 255, 255, 0.25), 0px 0px 0 1px rgba(0, 0, 0, 0.25);
  }

  ${(p) =>
    p.selected &&
    `
    box-shadow: 0px 0px 0 1px rgba(255, 255, 255, 0.5), 0px 0px 0 1px rgba(0, 0, 0, 0.5);

    :hover {
      box-shadow: 0px 0px 0 1px rgba(255, 255, 255, 0.5), 0px 0px 0 1px rgba(0, 0, 0, 0.5);
    }
  `};
`;
